import React, { useEffect, useState, useCallback, useRef } from "react";
import { requestAuthGet, requestAuthPost } from "../../api/authInstance";

const getSafeExpirationDate = (exp) => {
  if (!exp) {
    return null;
  }

  const expDate = new Date(exp);
  if (Number.isNaN(expDate.getTime())) {
    return null;
  }

  expDate.setDate(expDate.getDate() - 1);
  return expDate;
};

// Set authentication context
const AuthContext = React.createContext({
  userData: null,
  loading: true,
});
// Set authentication provider
export const AuthProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isRefreshingRef = useRef(false);

  // Function to fetch authentication information
  const fetchAuthData = useCallback(async () => {
    if (isRefreshingRef.current) {
      return;
    }

    isRefreshingRef.current = true;
    setLoading(true);
    try {
      const response = await requestAuthGet("auth/me");
      const { data } = response.data;
      setUserData(data);
      if (data.token) {
        if (localStorage.getItem("token_user") !== data.token) {
          localStorage.setItem("token_user", data.token);
        }

        const expDate = getSafeExpirationDate(data.exp);
        if (expDate) {
          localStorage.setItem("token_expiration", expDate.toISOString());
        } else {
          localStorage.removeItem("token_expiration");
        }
      }
    } catch (error) {
      setUserData(null);
    } finally {
      isRefreshingRef.current = false;
      setLoading(false);
    }
  }, []);

  const logout = async () => {
    try {
      if (userData?.identifier) {
        await requestAuthPost(`auth/logout/${userData.identifier}`);
      }
    } catch (error) {
      // Silently handle — clear local state regardless of API result
    }
    localStorage.clear();
    setUserData(null);
    setLoading(false);
    window.location.href = "/signin";
  };

  // useEffect to fetch authentication data on component mount
  useEffect(() => {
    fetchAuthData();
  }, [fetchAuthData]);

  useEffect(() => {
    let interval;
    // console.log("🔄 Token check effect initialized");

    const checkTokenExpiration = () => {
      // console.log("⏰ Running token expiration check...");
      const expirationDate = localStorage.getItem("token_expiration");
      if (!expirationDate) {
        // console.log("❌ No expiration date found in localStorage");
        return;
      }

      const expDate = new Date(expirationDate);
      if (Number.isNaN(expDate.getTime())) {
        localStorage.removeItem("token_expiration");
        return;
      }

      const now = new Date();

      // const timeRemaining = (expDate.getTime() - now.getTime()) / 1000 / 60;
      // console.log(
      //   `⏳ Time remaining until token refresh: ${timeRemaining.toFixed(2)} minutes`,
      // );

      if (now > expDate) {
        // console.log("🔑 Token expired, refreshing...");
        if (!isRefreshingRef.current) {
          fetchAuthData();
        }
        return;
      }

      // Only check isToday after confirming it hasn't expired
      const isToday = expDate.toDateString() === now.toDateString();
      if (!isToday) {
        // console.log("📅 Expiration is not today, skipping check");
        if (interval) clearInterval(interval);
        return;
      }
    };

    // Run check immediately
    checkTokenExpiration();

    // Set up interval
    interval = setInterval(checkTokenExpiration, 10000);

    return () => {
      if (interval) {
        // console.log("🛑 Cleaning up interval");
        clearInterval(interval);
      }
    };
  }, [fetchAuthData]);

  return (
    <AuthContext.Provider value={{ userData, loading, fetchAuthData, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Set authentication hook
export const useAuth = () => {
  return React.useContext(AuthContext);
};
